import React from 'react';

import { StyleSheet, View } from 'react-native';

import ActionButton, { ActionButtonSizes } from 'components/action-button';
import { useAppTakeoverCart } from 'components/app-takeover/use-app-takeover-cart';
import { PaymentProcessLoading } from 'components/payment-process-loading';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { ClickEventComponentNames } from 'state/amplitude';
import { useCartContext } from 'state/cart';
import { t } from 'state/intl/translations';
import { primitive } from 'styles/constants/primitives';
import { TraceName, startTrace } from 'utils/performance/trace';

import { useCheckoutState } from '../../checkout-provider';
import { useCheckoutLoadingState } from '../../hooks';
import { useCheckoutSubmit } from '../../hooks/use-checkout-submit';
import { useCheckoutValidation } from '../../hooks/use-checkout-validation';

export const PlaceOrderButton = () => {
  const {
    submitOrder,
    ErrorDialog,
    wasCommitOrderCalled,
    commitOrderSuccess,
    commitOrderError,
  } = useCheckoutSubmit();
  const { validate } = useCheckoutValidation();
  const checkoutErrors = validate();
  const [, setCheckoutState] = useCheckoutState();
  const {
    isCheckoutLoading,
    isLoadingPaymentMethods,
    isOrderPriceLoading,
  } = useCheckoutLoadingState();

  const { hasPaymentProcessingTakeover } = useAppTakeoverCart();
  const hasFooterErrors = checkoutErrors.some(error => error.placement === 'footer');
  const { isOrderPricingRetryInProgress } = useCartContext();

  const _rawShowPaymentProcessLoading =
    hasPaymentProcessingTakeover &&
    wasCommitOrderCalled &&
    !commitOrderSuccess &&
    !commitOrderError;

  // debounce until after any native payment drawers dismiss
  // these mess up the animation modal's presentation. 1s is long enough.
  const showPaymentProcessLoading = useDebouncedValue(_rawShowPaymentProcessLoading, 1000);

  const handlePress = () => {
    if (checkoutErrors.length === 0) {
      startTrace(TraceName.PLACE_ORDER_TO_RECEIPT);
      submitOrder();
    }
    const dialogErrors = checkoutErrors.filter(error => error.placement === 'errorDialog');
    if (dialogErrors.length > 0) {
      // TODO: we may need to rethink this if we want take different actions based on error type
      setCheckoutState(prev => ({ ...prev, errorModalOpen: true }));
    }
  };

  let buttonText = t('placeOrder');

  if (isOrderPricingRetryInProgress) {
    buttonText = t('thisMayTakeAFewSeconds');
  } else if (isCheckoutLoading && !isLoadingPaymentMethods && !isOrderPriceLoading) {
    buttonText = t('placingOrderCheckoutBtn');
  }

  return (
    <>
      {showPaymentProcessLoading && <PaymentProcessLoading />}
      <View style={styles.buttonContainer}>
        <ActionButton
          onPress={handlePress}
          disabled={isCheckoutLoading || hasFooterErrors}
          size={ActionButtonSizes.LARGE}
          isLoading={isCheckoutLoading}
          fullWidth
          eventAttributes={{
            component: ClickEventComponentNames.BUTTON,
            name: 'Place Secure Order',
          }}
        >
          {buttonText}
        </ActionButton>
      </View>

      {/* make sure any payment processing modal is hidden before showing the error dialog or things can get stuck on ios */}
      {!showPaymentProcessLoading && <ErrorDialog />}
    </>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    width: '100%',
    paddingHorizontal: 16,
    marginTop: primitive.spacing.$4,
  },
});
